import type { JSX } from 'react';
import { cn } from '../lib/cn.js';

/**
 * L'INTERRUPTEUR du kit Parametres (gabarit v2.2) : LE composant unique qui
 * remplace les cases a cocher natives des panneaux de reglages. Une seule
 * implementation dans tout le produit (le prototype en montrait deux, button
 * role=switch et input reskinne : c'est celle-ci qui reste).
 *
 * Geometrie figee : piste 2rem x 1.125rem, bouton 0.75rem, course 0.875rem.
 *
 * L'ETAT ACTIF EST EN ENCRE (--ink-900), JAMAIS EN LIME : un reglage n'est
 * pas l'action principale d'un ecran, et le lime plein n'a qu'un titulaire
 * par ecran. Meme doctrine que l'etat actif du selecteur de vues.
 *
 * VERROUILLE : il garde sa forme et son etat, il perd sa main et son
 * interaction. Il n'est PAS efface (un reglage qu'on ne peut pas changer doit
 * rester lisible) et sa raison s'affiche au survol (`lockedReason`). C'est
 * `aria-disabled`, pas `disabled` : l'element reste atteignable au clavier,
 * comme une entree grisee du selecteur de vues. Patron type : la premiere
 * pastille d'un catalogue de filtres est le repli, toujours visible ; sa
 * ligne porte alors la pastille "toujours visible" (SettingsRow `badge`) et
 * son interrupteur est verrouille avec la raison en title.
 */
export interface SwitchProps {
  /** Etat courant. */
  checked: boolean;
  /** Bascule demandee par l'utilisateur. Jamais appele quand `locked`. */
  onChange?: (checked: boolean) => void;
  /** Verrouille : etat fige, plus d'interaction, raison au survol. */
  locked?: boolean;
  /** La raison du verrou, montree au survol (title). */
  lockedReason?: string;
  /** Libelle accessible, obligatoire : l'interrupteur ne porte aucun texte. */
  'aria-label': string;
  className?: string;
}

/** Motion par les tokens (background et course du bouton). */
const MOTION = {
  transitionDuration: 'var(--dur-base)',
  transitionTimingFunction: 'var(--ease-standard)',
} as const;

export function Switch({
  checked,
  onChange,
  locked = false,
  lockedReason,
  className,
  'aria-label': ariaLabel,
}: SwitchProps): JSX.Element {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={ariaLabel}
      aria-disabled={locked || undefined}
      title={locked ? lockedReason : undefined}
      onClick={locked ? undefined : () => onChange?.(!checked)}
      style={MOTION}
      className={cn(
        'relative h-[1.125rem] w-8 shrink-0 rounded-pill border-none p-0 transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-border',
        locked
          ? 'cursor-default bg-control-disabled-ink'
          : checked
            ? 'cursor-pointer bg-ink-900'
            : 'cursor-pointer bg-border-strong',
        className,
      )}
    >
      <span
        aria-hidden
        style={MOTION}
        className={cn(
          'absolute left-[0.1875rem] top-[0.1875rem] h-3 w-3 rounded-pill bg-surface transition-transform',
          checked && 'translate-x-[0.875rem]',
        )}
      />
    </button>
  );
}
